import React, { useEffect, useRef, useState } from 'react';
import { UseSlideoutModalFeatureParams, UseSlideoutModalFeatureReturn } from './types';

/**
 * useSlideoutModalFeature
 *
 * A custom React hook that encapsulates all interaction, event, and state management
 * logic for the SlideoutModal component. This hook handles:
 *
 * - Portal mounting state to prevent hydration issues
 * - Auto-focus management for accessibility
 * - Escape key press detection and handling
 * - Body scroll locking when modal is open
 * - Backdrop click detection and handler
 *
 * This separation allows the SlideoutModal component to focus purely on rendering
 * and layout concerns, while the hook manages all behavioral logic.
 *
 * @param params - Configuration object containing modal behavior settings
 * @returns Object with refs and handlers for use in the SlideoutModal component
 *
 * @example
 * const { modalRef, buttonRef, mounted, handleBackdropClick } = useSlideoutModalFeature({
 *   autoFocus: true,
 *   closeOnBackdropClick: true,
 *   closeOnEscape: true,
 *   isOpen: true,
 *   onClose: handleClose,
 * });
 */
export const useSlideoutModalFeature = ({
  autoFocus,
  closeOnBackdropClick,
  closeOnEscape,
  isOpen,
  onClose,
}: UseSlideoutModalFeatureParams): UseSlideoutModalFeatureReturn => {
  // ==================================================================
  // Refs for DOM element access
  // ==================================================================

  /**
   * Reference to the modal panel/container div element.
   * Used to determine if a click occurred inside or outside the modal.
   */
  const modalRef = useRef<HTMLDivElement>(null);

  /**
   * Reference to the close button element.
   * Used to apply focus when the modal opens (if autoFocus is enabled).
   */
  const buttonRef = useRef<HTMLButtonElement>(null);

  // ==================================================================
  // State management
  // ==================================================================

  /**
   * Tracks whether the component has mounted on the client.
   * Used for portal rendering safety - prevents rendering portal content
   * during server-side rendering, avoiding hydration mismatches.
   */
  const [mounted, setMounted] = useState(false);

  // ==================================================================
  // Effect: Mount tracking
  // ==================================================================

  /**
   * Sets the mounted flag to true on component mount.
   * Ensures portal rendering only happens after hydration is complete.
   * This runs once on mount and has no cleanup.
   */
  useEffect(() => {
    setMounted(true);
  }, []);

  // ==================================================================
  // Effect: Auto-focus management
  // ==================================================================

  /**
   * Manages focus on the close button when the modal opens.
   * Improves keyboard navigation and accessibility by ensuring
   * an actionable element has focus when the modal becomes visible.
   *
   * Only focuses if:
   * - autoFocus prop is true
   * - Modal is currently open
   * - Component has mounted (to avoid hydration issues)
   * - Button ref is available
   *
   * Dependencies: autoFocus, isOpen, mounted
   * Note: buttonRef intentionally excluded to avoid unnecessary focus calls
   */
  useEffect(() => {
    const shouldFocus = autoFocus && isOpen && mounted && buttonRef.current;

    if (shouldFocus) {
      buttonRef.current.focus();
    }
  }, [autoFocus, isOpen, mounted]);

  // ==================================================================
  // Effect: Inert state management
  // ==================================================================

  /**
   * Applies/removes the inert attribute directly on the modal panel.
   * React does not reliably map inert as a JSX prop in this setup,
   * so we manage it on the underlying DOM node.
   */
  useEffect(() => {
    const modalElement = modalRef.current;

    if (!modalElement) return;

    if (isOpen) {
      modalElement.removeAttribute('inert');
      return;
    }

    modalElement.setAttribute('inert', '');
  }, [isOpen, mounted]);

  // ==================================================================
  // Effect: Focus cleanup on close
  // ==================================================================

  /**
   * Ensures focus does not remain inside the modal when it closes.
   * This prevents accessibility warnings when hidden/inert state is applied
   * while a descendant still holds focus.
   */
  useEffect(() => {
    if (isOpen) return;

    const modalElement = modalRef.current;
    const activeElement = document.activeElement as HTMLElement | null;

    if (modalElement && activeElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }
  }, [isOpen]);

  // ==================================================================
  // Effect: Escape key handler
  // ==================================================================

  /**
   * Registers and manages the Escape key listener.
   * When the user presses Escape and closeOnEscape is enabled, the modal closes.
   *
   * Listener lifecycle:
   * - Added when modal opens AND closeOnEscape is true
   * - Removed when modal closes OR closeOnEscape becomes false
   * - Properly cleaned up on unmount
   *
   * The handler checks if the pressed key is 'Escape' and calls onClose if true.
   *
   * Dependencies: isOpen, closeOnEscape, onClose
   */
  useEffect(() => {
    // Only attach listener if modal is open and escape key closing is enabled
    if (!isOpen || !closeOnEscape) return;

    /**
     * Handler function for keydown events.
     * Checks if the Escape key was pressed and triggers the close callback.
     */
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Register the keydown listener on the document
    document.addEventListener('keydown', handleEscape);

    // Cleanup: Remove listener when dependencies change or component unmounts
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // ==================================================================
  // Effect: Body scroll lock
  // ==================================================================

  /**
   * Locks and unlocks the document body scroll based on modal open state.
   * Prevents background content from scrolling while the modal overlay is visible.
   *
   * When modal opens:
   * - Sets document.body.style.overflow to 'hidden' (disables scrolling)
   *
   * When modal closes:
   * - Restores document.body.style.overflow to 'unset' (allows scrolling)
   *
   * The cleanup function always restores the scroll state to prevent
   * permanent scroll lock if the effect re-runs unexpectedly.
   *
   * Dependencies: isOpen
   */
  useEffect(() => {
    if (isOpen) {
      // Disable scroll on the document body
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scroll on the document body
      document.body.style.overflow = 'unset';
    }

    // Always restore overflow on cleanup or when dependency changes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ==================================================================
  // Event handlers
  // ==================================================================

  /**
   * Handles backdrop/overlay click events.
   * Determines whether a click occurred inside or outside the modal content.
   *
   * If closeOnBackdropClick is enabled and the click occurred outside the modal
   * (on the backdrop itself), this handler triggers the onClose callback.
   *
   * Algorithm:
   * 1. Return early if closeOnBackdropClick is disabled
   * 2. Get the modal element from ref
   * 3. Get the click target from the event
   * 4. Check if the target is inside the modal using contains()
   * 5. If clicked outside modal, call onClose()
   *
   * @param event - The React click event from the backdrop container
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    // If backdrop closing is disabled, don't process the click
    if (!closeOnBackdropClick) return;

    // Get references to the modal element and click target
    const modalElement = modalRef.current;
    const target = event.target as Node | null;

    // Check if the click target is within the modal element
    const clickedInsideModal = modalElement?.contains(target);

    // If modal exists and click was outside it, trigger close
    if (modalElement && !clickedInsideModal) {
      onClose();
    }
  };

  // ==================================================================
  // Return interface
  // ==================================================================

  /**
   * Return the refs and handlers that SlideoutModal needs.
   * These are used by the component to manage focus, detect interactions,
   * and control the modal's appearance and behavior.
   */
  return {
    buttonRef,
    handleBackdropClick,
    modalRef,
    mounted,
  };
};

export default useSlideoutModalFeature;
